/**
 * Broker Requests List Page
 *
 * Broker-side page to browse client requests and submit offers.
 * Part of the client requests marketplace.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Badge from '../../components/base/Badge';
import Modal from '../../components/base/Modal';
import { useAuth } from '../../hooks/useAuth';
import { listAvailableRequests } from '../../services/clientRequestService';
import { formatAmount, formatDateLatn } from '../../utils/numberFormat';

interface ClientRequest {
  id: string;
  propertyType: string;
  requestType: 'buy' | 'rent';
  city: string;
  districts: string[];
  budgetMin: number;
  budgetMax: number;
  bedrooms: number;
  bathrooms: number;
  minArea: number;
  moveInDate: string;
  specialRequirements: string;
  isPrivate: boolean;
  createdAt: string;
  leadPrice: number;
  isPurchased: boolean;
  hasResponded: boolean;
}

// Real data now comes from GET /api/client-requests/available (agents only).
// The available feed masks contact info + some fields until a lead is purchased,
// so unavailable fields below are mapped to empty/0 (the card renders them
// conditionally).

const propertyTypeIcons: Record<string, string> = {
  apartment: 'ri-building-4-line',
  villa: 'ri-home-5-line',
  floor: 'ri-stack-line',
  land: 'ri-landscape-line',
  office: 'ri-building-line',
  shop: 'ri-store-line',
};

const propertyTypeLabels: Record<string, { ar: string; en: string }> = {
  apartment: { ar: 'شقة', en: 'Apartment' },
  villa: { ar: 'فيلا', en: 'Villa' },
  floor: { ar: 'دور', en: 'Floor' },
  land: { ar: 'أرض', en: 'Land' },
  office: { ar: 'مكتب', en: 'Office' },
  shop: { ar: 'محل', en: 'Shop' },
};

export default function BrokerRequestsList() {
  const { t, i18n } = useTranslation('clientRequests');
  const isRTL = i18n.language === 'ar';
  const { session } = useAuth();
  const token = session?.token ?? null;

  const [requests, setRequests] = useState<ClientRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notAgent, setNotAgent] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ClientRequest | null>(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let active = true;
    (async () => {
      setLoading(true); setLoadError(null); setNotAgent(false);
      try {
        const res = await listAvailableRequests(token);
        if (!active) return;
        // Map the masked available-feed records onto the card's display shape.
        setRequests((res.data || []).map((r): ClientRequest => ({
          id: r.id,
          propertyType: (r.propertyType || '').toLowerCase(),
          requestType: r.listingType === 'RENT' ? 'rent' : 'buy',
          city: (isRTL ? r.city?.nameAr : r.city?.name) || r.city?.name || '',
          districts: [],
          budgetMin: r.minPrice ?? 0,
          budgetMax: r.maxPrice ?? 0,
          bedrooms: r.minBedrooms ?? 0,
          bathrooms: 0,
          minArea: r.minSqft ? Math.round(r.minSqft / 10.7639) : 0,
          moveInDate: '',
          specialRequirements: '',
          isPrivate: false,
          createdAt: r.createdAt || '',
          leadPrice: r.leadPrice ?? 0,
          isPurchased: false,
          hasResponded: false,
        })));
      } catch (e) {
        if (!active) return;
        const err = e as { status?: number };
        if (err?.status === 403) setNotAgent(true);
        else setLoadError(t('broker.loadError', 'تعذّر تحميل الطلبات. حاول مرة أخرى.'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, isRTL, t]);

  // Filters
  const [filters, setFilters] = useState({
    propertyType: '',
    city: '',
    requestType: '',
    budgetMin: '',
    budgetMax: '',
  });

  const filteredRequests = requests.filter((req) => {
    if (filters.propertyType && req.propertyType !== filters.propertyType) return false;
    if (filters.city && req.city !== filters.city) return false;
    if (filters.requestType && req.requestType !== filters.requestType) return false;
    return true;
  });

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'budgetHigh':
        return b.budgetMax - a.budgetMax;
      case 'budgetLow':
        return a.budgetMin - b.budgetMin;
      default:
        return 0;
    }
  });

  // Western digits with "," grouping in both languages (numerals policy; see numberFormat.ts).
  const formatPrice = (price: number) => {
    return formatAmount(price);
  };

  const handleViewRequest = (request: ClientRequest) => {
    setSelectedRequest(request);
  };

  const handleSubmitOffer = (request: ClientRequest) => {
    setSelectedRequest(request);
    setShowOfferModal(true);
  };

  const clearFilters = () => {
    setFilters({
      propertyType: '',
      city: '',
      requestType: '',
      budgetMin: '',
      budgetMax: '',
    });
  };

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Page Header */}
      <header className="relative pt-8 pb-6">
        <div className="absolute inset-x-0 top-0 -z-10 h-[200px] bg-[var(--pv-color-surface-soft)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-[var(--pv-color-text)]">
            {t('broker.title', 'سوق الطلبات')}
          </h1>
          <p className="text-[var(--pv-color-text-muted)] mt-1">
            {t('broker.subtitle', 'تصفح طلبات العملاء وقدم عروضك')}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <Card variant="bordered" padding="md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[var(--pv-color-text)]">
                  {t('broker.filters.title', 'تصفية النتائج')}
                </h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-[var(--pv-color-primary)] hover:underline"
                >
                  {t('broker.filters.clearAll', 'مسح الكل')}
                </button>
              </div>

              <div className="space-y-4">
                {/* Property Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
                    {t('broker.filters.propertyType', 'نوع العقار')}
                  </label>
                  <select
                    value={filters.propertyType}
                    onChange={(e) => setFilters({ ...filters, propertyType: e.target.value })}
                    className="w-full px-3 py-2 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-lg text-[var(--pv-color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                  >
                    <option value="">{t('general.any', 'الكل')}</option>
                    <option value="apartment">{isRTL ? 'شقة' : 'Apartment'}</option>
                    <option value="villa">{isRTL ? 'فيلا' : 'Villa'}</option>
                    <option value="office">{isRTL ? 'مكتب' : 'Office'}</option>
                    <option value="land">{isRTL ? 'أرض' : 'Land'}</option>
                    <option value="shop">{isRTL ? 'محل' : 'Shop'}</option>
                  </select>
                </div>

                {/* City Filter */}
                <div>
                  <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
                    {t('broker.filters.city', 'المدينة')}
                  </label>
                  <select
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                    className="w-full px-3 py-2 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-lg text-[var(--pv-color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                  >
                    <option value="">{t('general.any', 'الكل')}</option>
                    <option value="الرياض">{isRTL ? 'الرياض' : 'Riyadh'}</option>
                    <option value="جدة">{isRTL ? 'جدة' : 'Jeddah'}</option>
                    <option value="الدمام">{isRTL ? 'الدمام' : 'Dammam'}</option>
                    <option value="مكة المكرمة">{isRTL ? 'مكة المكرمة' : 'Mecca'}</option>
                  </select>
                </div>

                {/* Transaction Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
                    {t('broker.filters.transactionType', 'نوع الصفقة')}
                  </label>
                  <select
                    value={filters.requestType}
                    onChange={(e) => setFilters({ ...filters, requestType: e.target.value })}
                    className="w-full px-3 py-2 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-lg text-[var(--pv-color-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                  >
                    <option value="">{t('general.any', 'الكل')}</option>
                    <option value="rent">{t('transactionType.rent', 'إيجار')}</option>
                    <option value="buy">{t('transactionType.buy', 'شراء')}</option>
                  </select>
                </div>
              </div>
            </Card>
          </aside>

          {/* Requests List */}
          <div className="flex-1">
            {!token ? (
              <Card variant="bordered" padding="lg" className="text-center">
                <div className="py-12 text-[var(--pv-color-text-muted)]">{t('broker.signIn', 'سجّل الدخول كوسيط معتمد لتصفح الطلبات.')}</div>
              </Card>
            ) : loading ? (
              <Card variant="bordered" padding="lg" className="text-center">
                <div className="py-12 text-[var(--pv-color-text-muted)]"><i className="ri-loader-4-line animate-spin text-2xl mr-2" />{t('loadingText', 'جاري التحميل...')}</div>
              </Card>
            ) : notAgent ? (
              <Card variant="bordered" padding="lg" className="text-center">
                <div className="py-12">
                  <i className="ri-shield-user-line text-3xl text-[var(--pv-color-text-muted)] mb-3 block" />
                  <h3 className="font-semibold text-[var(--pv-color-text)] mb-1">{t('broker.agentsOnly', 'متاح للوسطاء المعتمدين فقط')}</h3>
                  <p className="text-[var(--pv-color-text-muted)]">{t('broker.agentsOnlyDesc', 'يجب أن يكون حسابك وسيطاً معتمداً لتصفح طلبات العملاء.')}</p>
                </div>
              </Card>
            ) : loadError ? (
              <Card variant="bordered" padding="lg" className="text-center">
                <div className="py-12 text-red-600">{loadError}</div>
              </Card>
            ) : (
              <>
            {/* Sort & Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[var(--pv-color-text-muted)]">
                {t('counts.results', { count: sortedRequests.length })}
              </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-lg text-[var(--pv-color-text)] text-sm focus:outline-none"
              >
                <option value="newest">{t('broker.sort.newest', 'الأحدث')}</option>
                <option value="budgetHigh">{t('broker.sort.budgetHigh', 'الميزانية: الأعلى')}</option>
                <option value="budgetLow">{t('broker.sort.budgetLow', 'الميزانية: الأقل')}</option>
              </select>
            </div>

            {/* Requests Grid */}
            {sortedRequests.length === 0 ? (
              <Card variant="bordered" padding="lg" className="text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-[var(--pv-color-surface-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-3xl text-[var(--pv-color-text-muted)]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-2">
                    {t('broker.noResults', 'لا توجد طلبات مطابقة')}
                  </h3>
                  <p className="text-[var(--pv-color-text-muted)]">
                    {t('broker.noResultsDesc', 'جرب تغيير معايير البحث')}
                  </p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {sortedRequests.map((request) => (
                  <Card key={request.id} variant="bordered" padding="md" className="hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Property Type Icon */}
                      <div className="w-16 h-16 bg-[var(--pv-color-primary)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${propertyTypeIcons[request.propertyType] || 'ri-home-line'} text-2xl text-[var(--pv-color-primary)]`}></i>
                      </div>

                      {/* Request Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant={request.requestType === 'buy' ? 'primary' : 'default'}>
                                {request.requestType === 'buy' ? t('transactionType.buy', 'شراء') : t('transactionType.rent', 'إيجار')}
                              </Badge>
                              {request.isPrivate && (
                                <Badge variant="warning" icon="ri-lock-line">
                                  {t('privacy.private', 'خاص')}
                                </Badge>
                              )}
                              {request.hasResponded && (
                                <Badge variant="success" icon="ri-check-line">
                                  {t('broker.alreadyResponded', 'قدمت عرضاً')}
                                </Badge>
                              )}
                            </div>
                            <h3 className="text-lg font-semibold text-[var(--pv-color-text)]">
                              {propertyTypeLabels[request.propertyType]?.[isRTL ? 'ar' : 'en'] || request.propertyType}
                              {' '}{t('in', 'في')} {request.city}
                            </h3>
                            <p className="text-[var(--pv-color-text-muted)] mt-1">
                              <i className="ri-map-pin-line mr-1"></i>
                              {request.districts.join('، ')}
                            </p>
                          </div>

                          <div className="text-end">
                            <p className="text-sm text-[var(--pv-color-text-muted)]">
                              {t('budget.title', 'الميزانية')}
                            </p>
                            <p className="font-bold text-[var(--pv-color-primary)]">
                              {formatPrice(request.budgetMin)} - {formatPrice(request.budgetMax)}
                            </p>
                            <p className="text-xs text-[var(--pv-color-text-muted)]">
                              {t('general.sar', 'ريال')}
                              {request.requestType === 'rent' && ` / ${t('rentalDuration.annual', 'سنوي')}`}
                            </p>
                          </div>
                        </div>

                        {/* Specs */}
                        <div className="flex items-center gap-4 mt-3 text-sm text-[var(--pv-color-text-muted)] flex-wrap">
                          {request.bedrooms > 0 && (
                            <span>
                              <i className="ri-hotel-bed-line mr-1"></i>
                              {t('counts.bedrooms', { count: request.bedrooms })}
                            </span>
                          )}
                          {request.bathrooms > 0 && (
                            <span>
                              <i className="ri-drop-line mr-1"></i>
                              {t('counts.bathrooms', { count: request.bathrooms })}
                            </span>
                          )}
                          {request.minArea > 0 && (
                            <span>
                              <i className="ri-ruler-line mr-1"></i>
                              {request.minArea}+ {t('general.sqm', 'م²')}
                            </span>
                          )}
                          {request.moveInDate && (
                            <span>
                              <i className="ri-calendar-check-line mr-1"></i>
                              {formatDateLatn(request.moveInDate, isRTL ? 'ar-SA' : 'en-US')}
                            </span>
                          )}
                        </div>

                        {/* Special Requirements */}
                        {request.specialRequirements && (
                          <p className="mt-2 text-sm text-[var(--pv-color-text-muted)] line-clamp-2">
                            <i className="ri-file-list-line mr-1"></i>
                            {request.specialRequirements}
                          </p>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--pv-border-soft)]">
                          <div className="flex items-center gap-2 text-sm text-[var(--pv-color-text-muted)]">
                            <i className="ri-time-line"></i>
                            {formatDateLatn(request.createdAt, isRTL ? 'ar-SA' : 'en-US')}
                          </div>

                          <div className="flex items-center gap-2">
                            {!request.isPurchased ? (
                              <>
                                <span className="text-sm text-[var(--pv-color-text-muted)]">
                                  {t('broker.leadPrice', 'سعر الطلب')}:
                                  <span className="font-bold text-[var(--pv-color-primary)] mx-1">{formatAmount(request.leadPrice)}</span>
                                  {t('general.sar', 'ريال')}
                                </span>
                                <button
                                  onClick={() => handleViewRequest(request)}
                                  className="px-4 py-2 text-sm font-medium text-[var(--pv-color-primary)] border border-[var(--pv-color-primary)] rounded-lg hover:bg-[var(--pv-color-primary)]/10 transition-colors"
                                >
                                  {t('broker.viewRequest', 'عرض الطلب')}
                                </button>
                                <button
                                  onClick={() => handleSubmitOffer(request)}
                                  className="px-4 py-2 text-sm font-medium bg-[var(--pv-color-primary)] text-white rounded-lg hover:shadow-md transition-all"
                                >
                                  {t('broker.submitOffer', 'قدم عرضك')}
                                </button>
                              </>
                            ) : (
                              <>
                                <Badge variant="success" icon="ri-check-line">
                                  {t('broker.leadPurchased', 'تم شراء البيانات')}
                                </Badge>
                                <button
                                  onClick={() => handleViewRequest(request)}
                                  className="px-4 py-2 text-sm font-medium bg-[var(--pv-color-primary)] text-white rounded-lg hover:bg-[var(--pv-color-primary)]/90 transition-colors"
                                >
                                  {t('broker.contactClient', 'تواصل مع العميل')}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Submit Offer Modal */}
      <Modal
        isOpen={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        title={t('offer.title', 'قدم عرضك')}
        size="lg"
      >
        {selectedRequest && (
          <SubmitOfferForm
            request={selectedRequest}
            onClose={() => setShowOfferModal(false)}
            isRTL={isRTL}
          />
        )}
      </Modal>

      <Footer />
    </div>
  );
}

// Submit Offer Form Component
interface SubmitOfferFormProps {
  request: ClientRequest;
  onClose: () => void;
  isRTL: boolean;
}

function SubmitOfferForm({ request: _request, onClose, isRTL: _isRTL }: SubmitOfferFormProps) {
  const { t } = useTranslation('clientRequests');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    propertyTitle: '',
    propertyDescription: '',
    price: '',
    location: '',
    message: '',
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onClose();
    } catch (error) {
      console.error('Error submitting offer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--pv-color-text-muted)]">
        {t('offer.subtitle', 'صف العقار الذي تريد عرضه على العميل')}
      </p>

      {/* Property Title */}
      <div>
        <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
          {t('offer.propertyTitle', 'عنوان العقار')}
        </label>
        <input
          type="text"
          value={formData.propertyTitle}
          onChange={(e) => setFormData({ ...formData, propertyTitle: e.target.value })}
          placeholder={t('offer.propertyTitlePlaceholder', 'مثال: فيلا فاخرة في حي النرجس')}
          className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
        />
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
          {t('offer.price', 'السعر المعروض')}
        </label>
        <input
          type="number"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          placeholder={t('offer.pricePlaceholder', 'أدخل السعر')}
          className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
        />
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
          {t('offer.location', 'موقع العقار')}
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder={t('offer.locationPlaceholder', 'الحي والشارع')}
          className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
          {t('offer.propertyDescription', 'وصف العقار')}
        </label>
        <textarea
          value={formData.propertyDescription}
          onChange={(e) => setFormData({ ...formData, propertyDescription: e.target.value })}
          placeholder={t('offer.propertyDescriptionPlaceholder', 'اكتب وصفاً تفصيلياً للعقار...')}
          rows={3}
          className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20 resize-none"
        />
      </div>

      {/* Message to Client */}
      <div>
        <label className="block text-sm font-medium text-[var(--pv-color-text)] mb-2">
          {t('offer.message', 'رسالة للعميل')}
        </label>
        <textarea
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder={t('offer.messagePlaceholder', 'اكتب رسالة قصيرة للعميل...')}
          rows={2}
          className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20 resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--pv-border-soft)]">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-3 text-[var(--pv-color-text)] bg-[var(--pv-color-surface-soft)] rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {t('offer.cancel', 'إلغاء')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              {t('actions.submitting', 'جاري الإرسال...')}
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-2"></i>
              {t('offer.submit', 'إرسال العرض')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
